import { useRef, useState, useCallback } from 'react'
import { streamChatMessage, streamMockChatMessage, streamMockRegulationMessage, SendMessageRequest, StreamCompleteResult } from '@/api/chatService'

const STREAM_TIMEOUT_MS = 120_000 // 2 minutes

interface UseSendMessageOptions {
  onChunk?: (content: string) => void
  onStatus?: (status: string) => void
  onComplete?: (result: StreamCompleteResult) => void
  onError?: (error: Error) => void
}

/**
 * Custom hook for streaming chat messages from the backend.
 *
 * Replaces useMutation — TanStack Query mutations don't support
 * progressive state updates during a request (needed for streaming).
 *
 * Uses a ref for options to avoid stale closures in the async loop.
 */
export function useSendMessage(options?: UseSendMessageOptions) {
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Store options in a ref so the async loop always has the latest callbacks
  const optionsRef = useRef(options)
  optionsRef.current = options

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsStreaming(false)
  }, [])

  const sendMessage = useCallback(async (request: SendMessageRequest) => {
    // Cancel any in-progress stream
    cancel()

    const abortController = new AbortController()
    abortControllerRef.current = abortController
    setIsStreaming(true)

    // Safety timeout — abort if stream hangs
    const timeoutId = setTimeout(() => {
      abortController.abort()
    }, STREAM_TIMEOUT_MS)

    let completed = false

    const msg = request.message.trim()
    const stream = msg === '__mock_pdf'
      ? streamMockChatMessage(abortController.signal)
      : msg === '__mock_regulation'
      ? streamMockRegulationMessage(abortController.signal)
      : streamChatMessage(request, abortController.signal)

    try {
      for await (const chunk of stream) {
        if (abortController.signal.aborted) break

        if (chunk.error) {
          const err = new Error(chunk.error)
          err.name = 'RefusalError'
          throw err
        }

        console.debug('[stream] chunk:', { done: chunk.done, hasContent: !!chunk.content, hasStatus: !!chunk.status, contentLen: chunk.content?.length })

        if (chunk.done) {
          completed = true
          optionsRef.current?.onComplete?.({
            messageId: chunk.message_id!,
            conversationId: chunk.conversation_id!,
            title: chunk.title,
            citedResponse: chunk.cited_response,
            citations: chunk.citations,
            sources: chunk.sources,
          })
        } else if (chunk.status) {
          console.debug('[stream] status:', chunk.status)
          optionsRef.current?.onStatus?.(chunk.status)
        } else if (chunk.content) {
          optionsRef.current?.onChunk?.(chunk.content)
        }
      }

      // Stream ended without a done/error chunk (connection dropped)
      if (!completed && !abortController.signal.aborted) {
        optionsRef.current?.onError?.(
          new Error('The response was interrupted. Please try again.'),
        )
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        // Check if abort was from our timeout vs user cancel
        if (!abortControllerRef.current) {
          // User cancelled — no error to show
        } else {
          optionsRef.current?.onError?.(
            new Error('The request timed out. Please try again.'),
          )
        }
      } else {
        console.error('Stream error:', error)
        optionsRef.current?.onError?.(
          error instanceof Error ? error : new Error(String(error)),
        )
      }
    } finally {
      clearTimeout(timeoutId)
      setIsStreaming(false)
      abortControllerRef.current = null
    }
  }, [cancel])

  return { sendMessage, cancel, isStreaming }
}
